package de.oevbs.kunde.kundenportal2.services.inbox

import de.oevbs.businesspartner.bpcrmservice2.common.model.LineOfBusiness
import de.oevbs.kosmos.ui.overlays.modals.BusyTracker
import de.oevbs.kunde.kundenportal2.authentication.PortalSession
import de.oevbs.kunde.kundenportal2.authentication.PortalSessionMiddleware
import de.oevbs.kunde.kundenportal2.components.globalInitTracker
import de.oevbs.kunde.kundenportal2.model.services.inbox.*
import de.oevbs.kunde.kundenportal2.util.tryCatchAndHandleErrors
import de.oevbs.sfa.communication.sfaServiceOrigin
import de.oevbs.sfa.communication.use
import de.oevbs.sfa.data.time.now
import dev.fritz2.core.EmittingHandler
import dev.fritz2.core.RootStore
import dev.fritz2.core.SimpleHandler
import dev.fritz2.core.storeOf
import dev.fritz2.tracking.tracker
import io.ktor.client.*
import io.ktor.client.engine.js.*
import kotlinx.browser.window
import kotlinx.coroutines.Job
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.combine
import kotlinx.datetime.LocalDate

object FilterStore : RootStore<InboxFilter>(InboxFilter.emptyFilter, Job()) {

    /**
     * Bei dem Filter ist eine Besonderheit, dass die Eingabe des Patterns noch nicht unmittelbar die Suche anstößt,
     * sondern diese erst durch das Klicken auf die "Lupe" aktiviert wird. Dafür ist dieser spezielle Handler gedacht,
     * der zusätzlich prüft, ob überhaupt ein Pattern eingegeben ist. Bei einem leeren Suchwort wird natürlich
     * auch nicht gesucht.
     */
    val toggleState = handle { value ->
        if (value.pattern.isNotBlank())
            value.copy(
                patternState = when (value.patternState) {
                    PatternState.Readonly -> PatternState.Active
                    PatternState.Active -> PatternState.Readonly
                }
            )
        else value
    }

    init {
        PortalSession.onLogout { update(InboxFilter.emptyFilter) }
    }
}

object StoredNextYear : RootStore<Int>(LocalDate.now().year - 2, Job()) {
    init {
        PortalSession.onLogout { update(current) }
    }
}

class InboxStore(job: Job) : RootStore<List<Message>>(emptyList(), job) {

    private val client = object : InboxClientDefinition(window.location.sfaServiceOrigin) {
        override val client: HttpClient = HttpClient(Js, config)
    }.use(PortalSessionMiddleware)

    val filteredData: Flow<List<Message>> = data.combine(FilterStore.data) { messages, filter ->
        messages.filter { message ->
            filter.sparte == message.lineOfBusiness || filter.sparte == LineOfBusiness.Unbekannt
        }.filter { message ->
            if (filter.usePattern()) message.subject.lowercase().contains(filter.pattern.lowercase())
            else true
        }
    }

    private val initFailedStore = storeOf(false, Job())
    val initFailedFlow: Flow<Boolean> = initFailedStore.data

    private val loadMoreTracker = tracker().also { BusyTracker.attach(it) }
    val loadingMore: Flow<Boolean> = loadMoreTracker.data
    private val loadDetailsTracker = tracker()

    private val thisYear = LocalDate.now().year
    private var firstTwoYearsAlreadyLoaded = false

    val loadFirstTwoYears: SimpleHandler<Unit> = handle { messages ->
        if (firstTwoYearsAlreadyLoaded)
            return@handle messages

        globalInitTracker.track {
            val initData = loadYears(this.thisYear.minus(1), thisYear)
            if (initData == null) {
                initFailedStore.update(true)
                return@track messages
            }
            firstTwoYearsAlreadyLoaded = true
            initData
        }
    }

    val loadMore: EmittingHandler<Int, Int> = handleAndEmit { messages, newYear ->
        loadMoreTracker.track {
            val newMessages = loadYears(newYear)
            if (newMessages == null) messages
            else messages + newMessages.also { emit(newYear - 1) }
        }
    }

    private suspend fun loadYears(from: Int, to: Int? = null): List<Message>? {
        return tryCatchAndHandleErrors("Laden der Nachrichten der Zeitspanne from $from to $to") {
            client.messages(FromTo(from, to ?: from))
        }
    }

    val loadDetails: EmittingHandler<String, String> = handleAndEmit { messages, guid ->
        emit(guid)

        loadDetailsTracker.track {
            messages.map {
                if (it.id == guid && !it.detailsLoaded) {
                    loadDetails(guid) ?: it
                } else it
            }
        }
    }

    val loadDetailsData: Flow<Pair<Boolean, String>> = loadDetailsTracker.data.combine(loadDetails, ::Pair)

    init {
        /**
         * FIXME: Unbekanntes Issue im fritz2
         *  Muss konsumiert werden! Ansonsten erscheint auf `loadDetailsData` beim ersten Klick auf ein Collapsible
         *  kein Wert - vermutlich hängt dies mit dem Emittinghandler zusammen und es bräuchte einen Fix im fritz2.
         *  Sobald das Problem gelöst ist, solle dieser Aufruf entfernt werden können!
         */
        loadDetails handledBy { }

        loadMore handledBy StoredNextYear.update
    }

    private suspend fun loadDetails(guid: String): Message? {
        val message = tryCatchAndHandleErrors("Laden der Nachrichtendetails für guid $guid") {
            client.messageById(guid)
        } ?: return null
        return purgeMessage(message)
    }

    /**
     * Aus den Mockdaten kommen Avisedaten mit 00000000000000000000000000000000
     * Dieser werden hier ggf. aufbereitet.
     */
    private fun purgeMessage(message: Message): Message =
        if (message.guid.isEmpty() || message.guid.trim('0').isEmpty())
            message.copy(
                detailText = "Keine weiteren Informationen vorhanden.",
                detailsLoaded = true,
                isCollapsed = false
            )
        else
            message
}